/**
 * The OpenDocument package: what is in it, and what cannot be read from it.
 *
 * ODF is a ZIP with the same shape as the OOXML packages this codebase already
 * reads, so {@link openPackage} and the part-preserving {@link repackPackage}
 * apply unchanged. Three things are specific to ODF and are handled here.
 *
 * **The `mimetype` entry is load-bearing.** It must be first in the archive and
 * stored uncompressed; that is how a file is recognised as ODF without reading
 * the directory. `repackPackage` preserves entry order and stores `mimetype`
 * uncompressed, and {@link checkMimetypeEntry} asserts both after a save — a
 * document that stops being recognisable is a silent corruption from the user's
 * side, which is exactly the failure §13 says a save must not produce.
 *
 * **`META-INF/manifest.xml` is the only index.** ODF has no `_rels` parts; a
 * picture is referenced by path and described here. The manifest is also where
 * encryption is declared, per entry rather than per document.
 *
 * **Encryption is detected, not handled.** The catalog says
 * `detected-not-handled` for ODF passwords, and this module is what makes that
 * true: an encrypted document is refused with the password message rather than
 * opened as gibberish (§19 — never claim support that does not exist).
 */
import { HandlerError } from '../contract.js';
import { hasPart, openPackage, readPartText } from '../ooxml/package.js';
import type { OpcPackage } from '../ooxml/package.js';
import { attr, findElements } from './scan.js';

export const CONTENT_PART = 'content.xml';
export const STYLES_PART = 'styles.xml';
export const META_PART = 'meta.xml';
export const MANIFEST_PART = 'META-INF/manifest.xml';

export const ODF_MIME: Readonly<Record<string, string>> = {
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation',
};

export interface ManifestEntry {
  readonly path: string;
  readonly mediaType: string;
  readonly encrypted: boolean;
}

export interface OdfDocument {
  readonly pkg: OpcPackage;
  readonly contentXml: string;
  readonly stylesXml: string;
  readonly manifest: readonly ManifestEntry[];
  /** Declared package mimetype, empty when the entry is missing. */
  readonly mimetype: string;
  /** True when `styles.xml` was absent, so formatting is unresolved. */
  readonly stylesMissing: boolean;
}

/* ------------------------------------------------------------------ *
 * opening
 * ------------------------------------------------------------------ */

/**
 * Open an ODF package and read the parts every family needs.
 *
 * @param expectedMime The mimetype detection settled on. A package whose own
 *   `mimetype` entry disagrees is opened anyway but the disagreement is
 *   reported, because detection is more trustworthy than a string a writer
 *   could have got wrong — and refusing would mean refusing files that other
 *   applications open fine.
 */
export function openOdf(bytes: Uint8Array): OdfDocument {
  const pkg = openPackage(bytes);

  const mimetype = hasPart(pkg, 'mimetype') ? readPartText(pkg, 'mimetype').trim() : '';
  const manifest = hasPart(pkg, MANIFEST_PART)
    ? parseManifest(readPartText(pkg, MANIFEST_PART))
    : [];

  // Checked before anything is parsed: an encrypted content part inflates to
  // ciphertext, which would otherwise surface as a confusing XML error rather
  // than the true reason (§34).
  const encrypted = manifest.filter((e) => e.encrypted);
  if (encrypted.length > 0) {
    throw new HandlerError(
      'error.passwordProtected',
      `${encrypted.length} package entries are encrypted (${encrypted[0]!.path})`,
    );
  }

  if (!hasPart(pkg, CONTENT_PART)) {
    throw new HandlerError('error.damagedOrIncomplete', 'the package has no content.xml');
  }

  const stylesMissing = !hasPart(pkg, STYLES_PART);
  return {
    pkg,
    contentXml: readPartText(pkg, CONTENT_PART),
    stylesXml: stylesMissing ? '' : readPartText(pkg, STYLES_PART),
    manifest,
    mimetype,
    stylesMissing,
  };
}

/**
 * Entries declared by the manifest.
 *
 * The manifest is advisory for reading — the ZIP directory is what actually
 * says which parts exist — so a malformed one yields an empty list rather than
 * an error. It is authoritative for exactly one thing: encryption.
 */
export function parseManifest(xml: string): ManifestEntry[] {
  const out: ManifestEntry[] = [];
  for (const span of findElements(xml, 'manifest:file-entry')) {
    const path = attr(span.attrs, 'manifest:full-path');
    if (!path) continue;
    out.push({
      path,
      mediaType: attr(span.attrs, 'manifest:media-type') ?? '',
      // Present at all means encrypted; its children carry the algorithm and
      // salt, none of which this release can use.
      encrypted: !span.selfClosing
        && xml.slice(span.innerStart, span.innerEnd).includes('<manifest:encryption-data'),
    });
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * pictures
 * ------------------------------------------------------------------ */

const IMAGE_TYPES: Readonly<Record<string, string>> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  bmp: 'image/bmp', webp: 'image/webp', tiff: 'image/tiff', tif: 'image/tiff',
  svg: 'image/svg+xml', svm: 'image/x-svm', wmf: 'image/wmf', emf: 'image/emf',
};

export interface OdfImage {
  readonly part: string;
  readonly bytes: Uint8Array;
  readonly contentType: string;
}

/** Content type for a picture part, from the manifest first and the name after. */
export function imageContentType(
  doc: OdfDocument, path: string,
): string | null {
  const declared = doc.manifest.find((e) => e.path === path)?.mediaType;
  if (declared && declared.startsWith('image/')) return declared;
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
  return IMAGE_TYPES[ext] ?? null;
}

/**
 * An `xlink:href` resolved to a package part.
 *
 * ODF permits three forms: a package-relative path (`Pictures/x.png`), a
 * document-relative one with a leading `./`, and an external URL. Only the
 * first two name something inside the file; an external reference resolves to
 * null so the caller can report it as unresolved rather than fetching it —
 * §29 forbids the network entirely.
 */
export function resolveHref(href: string | null): string | null {
  if (!href) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return null;   // absolute URL of any scheme
  if (href.startsWith('//') || href.startsWith('/')) return null;
  return href.replace(/^\.\//, '');
}

/* ------------------------------------------------------------------ *
 * save-time checks
 * ------------------------------------------------------------------ */

/**
 * Assert that a saved package is still recognisable as ODF.
 *
 * Called from every ODF handler's `validateExport`. The two conditions are
 * structural rather than cosmetic: an ODF reader that finds `mimetype`
 * anywhere but first, or finds it compressed, is entitled to treat the file as
 * a plain ZIP.
 */
export function checkMimetypeEntry(pkg: OpcPackage, expected: string): string[] {
  const issues: string[] = [];
  const first = pkg.directory.entries[0];
  if (!first || first.name !== 'mimetype') {
    issues.push('the mimetype entry is no longer first in the package');
    return issues;
  }
  if (first.method !== 0) {
    issues.push('the mimetype entry is no longer stored uncompressed');
  }
  const written = readPartText(pkg, 'mimetype').trim();
  if (written !== expected) {
    issues.push(`the package mimetype changed: ${expected} → ${written}`);
  }
  return issues;
}

/**
 * Parts that exist but are neither read nor rendered, for the §31 screen.
 *
 * Naming them is the point: §19 forbids presenting partial support as
 * complete, and the honest form of that is to say which pieces of the file the
 * viewer did not look at.
 */
export function unreadOdfParts(doc: OdfDocument): string[] {
  const read = new Set([CONTENT_PART, STYLES_PART, META_PART, MANIFEST_PART, 'mimetype']);
  return doc.pkg.directory.entries
    .map((e) => e.name)
    .filter((name) => !read.has(name)
      && !name.endsWith('/')
      && !name.startsWith('Pictures/')
      && !name.startsWith('Thumbnails/'));
}
